import { useState, useEffect, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { DateInput } from '../../../../shared/components/DateInput.js';
import { PageHeader } from '../../../../shared/components/PageHeader.js';
import { Card } from '../../../../shared/components/Card.js';
import { Button } from '../../../../shared/components/Button.js';
import { Badge } from '../../../../shared/components/Badge.js';
import { Alert } from '../../../../shared/components/Alert.js';
import { EmptyState } from '../../../../shared/components/EmptyState.js';

/** A payment still to be reconciled (GET /v1/payments/reconciliation/unmatched). */
interface Unmatched {
  transactionId: string;
  utrNumber: string | null;
  bankReferenceNo: string | null;
  beneficiaryName: string | null;
  systemPaymentAmount: number;
  paymentDate: string | null;
  invoiceNumber: string | null;
  /** UNMATCHED when a statement was entered and differed; null when never reconciled. */
  lastStatus: string | null;
  varianceAmount: number | null;
}

/** What the bank statement says of the payment, as entered for a manual match. */
interface Statement {
  bankStatementRef: string;
  bankStatementDate: string;
  bankCreditAmount: string;
}

const EMPTY: Statement = { bankStatementRef: '', bankStatementDate: '', bankCreditAmount: '' };

const cell = { padding: '12px 14px', borderBottom: '1px solid var(--color-border-default)', verticalAlign: 'middle' } as const;
const head = { ...cell, fontSize: 12, letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--color-text-muted)', textAlign: 'left', background: 'var(--color-bg-muted)' } as const;
const input = { height: 38, padding: '0 10px', borderRadius: 8, border: '1px solid var(--color-border-default)', fontSize: 14 } as const;
const field = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted)' } as const;

/**
 * L15.07 — Reconciliation: every payment entered is matched to the line of the
 * bank statement that shows it. The statement's reference, date and amount
 * are entered, so the match records what the bank says and any variance is
 * measured against the payment; a payment whose amount differed stays listed.
 */
export function ReconciliationPage(): ReactNode {
  const { t } = useTranslation();
  // Amounts follow the tenant's DISPLAY_NUMBER_FORMAT, not a locale in the source.
  const { money } = useBusinessNumber();
  const { day } = useBusinessDay();
  const { token } = useAuth();
  const [items, setItems] = useState<Unmatched[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [matching, setMatching] = useState<string | null>(null);
  const [statement, setStatement] = useState<Statement>(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    apiRequest<Unmatched[]>('/v1/payments/reconciliation/unmatched', { token })
      .then(setItems)
      .catch(e => setError((e as Error).message))
      .finally(() => setLoading(false));
  };

  useEffect(load, [token]);

  const ready = statement.bankStatementRef.trim() && statement.bankStatementDate && statement.bankCreditAmount.trim();

  const handleMatch = async (transactionId: string) => {
    setBusy(true);
    setError(null);
    try {
      await apiRequest(`/v1/payments/reconciliation/match`, {
        token, method: 'POST',
        body: {
          transactionId, matchMethodCode: 'MANUAL',
          bankStatementRef: statement.bankStatementRef.trim(),
          bankStatementDate: statement.bankStatementDate,
          bankCreditAmount: Number(statement.bankCreditAmount),
        },
      });
      setMatching(null);
      setStatement(EMPTY);
      // A match whose amount differs stays on the list with its variance.
      load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const set = (k: keyof Statement) => (v: string) => setStatement((s) => ({ ...s, [k]: v }));

  return (
    <div className="tl-page" style={{ padding: 24 }}>
      <PageHeader title={t('finance.recon.title')} subtitle={t('finance.recon.subtitle')} />
      {error && <Alert tone="danger">{error}</Alert>}
      {loading && <p style={{ color: 'var(--color-text-muted)' }}>{t('finance.recon.loading')}</p>}
      {!loading && items.length === 0 && !error && <EmptyState title={t('finance.recon.empty')} />}
      {items.length > 0 && (
        <Card>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr>
                  <th style={head}>{t('finance.recon.colPaidOn')}</th>
                  <th style={head}>{t('finance.recon.colBill')}</th>
                  <th style={head}>{t('finance.recon.colVendor')}</th>
                  <th style={head}>{t('finance.recon.colUtr')}</th>
                  <th style={{ ...head, textAlign: 'right' }}>{t('finance.recon.colAmount')}</th>
                  <th style={head}>{t('finance.recon.colStatus')}</th>
                  <th style={head}>{t('finance.recon.colAction')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.transactionId} data-testid={`recon-row-${row.transactionId}`}>
                    <td style={cell}>{day(row.paymentDate)}</td>
                    <td style={cell}>{row.invoiceNumber ?? '\u2014'}</td>
                    <td style={cell}>{row.beneficiaryName ?? '\u2014'}</td>
                    <td style={{ ...cell, fontFamily: 'var(--font-mono, monospace)' }}>{row.utrNumber ?? row.bankReferenceNo ?? '\u2014'}</td>
                    <td style={{ ...cell, textAlign: 'right', fontWeight: 600 }}>{money(row.systemPaymentAmount)}</td>
                    <td style={cell}>
                      {row.lastStatus
                        ? <Badge tone="warning" dot>{t('finance.recon.statusVariance', { amount: money(row.varianceAmount ?? 0) })}</Badge>
                        : <Badge tone="info" dot>{t('finance.recon.statusNew')}</Badge>}
                    </td>
                    <td style={cell}>
                      {matching === row.transactionId ? (
                        <div className="tl-stack tl-stack--row" style={{ gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                          <label style={field}>
                            {t('finance.recon.statementRef')}
                            <input style={input} value={statement.bankStatementRef} onChange={(e) => set('bankStatementRef')(e.target.value)} />
                          </label>
                          <label style={field}>
                            {t('finance.recon.statementDate')}
                            <DateInput type="date" value={statement.bankStatementDate} onValueChange={set('bankStatementDate')} />
                          </label>
                          <label style={field}>
                            {t('finance.recon.statementAmount')}
                            <input style={{ ...input, width: 140 }} type="number" step="0.01" value={statement.bankCreditAmount} onChange={(e) => set('bankCreditAmount')(e.target.value)} />
                          </label>
                          <Button size="sm" variant="primary" disabled={busy || !ready} loading={busy} onClick={() => void handleMatch(row.transactionId)}>{t('finance.recon.match')}</Button>
                          <Button size="sm" variant="ghost" onClick={() => { setMatching(null); setStatement(EMPTY); }}>{t('finance.recon.cancel')}</Button>
                        </div>
                      ) : (
                        <Button size="sm" variant="secondary" onClick={() => { setMatching(row.transactionId); setStatement(EMPTY); }}>{t('finance.recon.match')}</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
